import * as stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { borderRadius, color } from '@figma/fpl-tokens/tokens.stylex';
import { Icon24Image, Icon24Text, Icon24Transform3d, Icon24VideoLarge, Icon24Voice } from '@figma/fpl-icons';
import { AssetThumbnail } from '@/components/Common/AssetThumbnail';
import { I18N_KEYS } from '@/language/keys';
import { useResolvedAssets } from '@/state/workflow.state';
import { thumbnailSource } from '@/utils/assetUrls';
import type { ThumbnailSource } from '@/utils/assetUrls';
import type { MediaAsset, TextAsset } from '@/types/api/assets';

const THUMBNAIL_BORDER_WIDTH = 1;

const typeIcons = {
  image: Icon24Image,
  video: Icon24VideoLarge,
  text: Icon24Text,
  audio: Icon24Voice,
  '3D': Icon24Transform3d,
};

export function ToolOutputThumbnail({
  asset,
  selected,
  fit = 'cover',
}: {
  asset: MediaAsset | TextAsset;
  selected: boolean;
  fit?: 'contain' | 'cover' | 'fitImage';
}) {
  const { t } = useTranslation();
  const resolvedAssets = useResolvedAssets();
  let source: ThumbnailSource = { kind: 'none' };
  if (asset.type === 'image' || asset.type === 'video' || asset.type === '3D') {
    source =
      asset.type === 'video' && asset.url && asset.thumbnailUrl === asset.url
        ? { kind: 'firstFrame', src: asset.url }
        : thumbnailSource(asset, resolvedAssets);
  }
  const hasImageThumbnail = asset.type === 'image' && source.kind !== 'none';
  const coverImage = fit === 'cover' && hasImageThumbnail;
  const fitImage = fit === 'fitImage' && hasImageThumbnail;
  const showFitImageFallback = fit === 'fitImage' && !hasImageThumbnail;
  const Icon = typeIcons[asset.type];
  return (
    <div
      {...stylex.props(
        styles.tile,
        !coverImage && styles.borderedTile,
        fitImage && styles.fitImageContainer,
        showFitImageFallback && styles.fitImageFallback,
      )}
    >
      <div
        {...stylex.props(
          styles.artwork,
          fitImage && styles.fitImageContainer,
          coverImage && styles.coverImage,
          !selected && styles.unselectedArtwork,
        )}
      >
        <AssetThumbnail
          source={source}
          alt=""
          draggable={false}
          {...stylex.props(
            styles.media,
            fit !== 'cover' && styles.contain,
            fitImage && styles.fitImageMedia,
            asset.type === 'video' && styles.video,
          )}
        />
      </div>
      {!hasImageThumbnail && (
        <div
          role={asset.type === 'video' ? 'img' : undefined}
          aria-label={asset.type === 'video' ? t(I18N_KEYS.GENERAL.FILE_TYPES.VIDEO, { count: 1 }) : undefined}
          {...stylex.props(styles.icon, !selected && styles.unselectedIcon)}
        >
          <Icon {...stylex.props(asset.type === 'video' && styles.videoIcon)} />
        </div>
      )}
    </div>
  );
}

const styles = stylex.create({
  tile: {
    width: '100%',
    height: '100%',
    flexShrink: 0,
    position: 'relative',
    overflow: 'hidden',
    borderRadius: borderRadius.medium,
  },
  artwork: { width: '100%', height: '100%' },
  coverImage: { backgroundColor: '#fff' },
  borderedTile: {
    backgroundColor: color.bgSecondary,
    borderWidth: THUMBNAIL_BORDER_WIDTH,
    borderStyle: 'solid',
    borderColor: color.border,
    boxSizing: 'border-box',
  },
  unselectedArtwork: { opacity: 0.4 },
  unselectedIcon: { opacity: 0.8 },
  media: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  contain: { objectFit: 'contain' },
  fitImageContainer: { width: 'fit-content', height: 'auto' },
  fitImageMedia: {
    width: 'auto',
    height: 'auto',
    maxWidth: 190 - THUMBNAIL_BORDER_WIDTH * 2,
    maxHeight: 130 - THUMBNAIL_BORDER_WIDTH * 2,
  },
  fitImageFallback: { width: 190, height: 130 },
  video: { filter: 'brightness(0.85)' },
  videoIcon: {
    '--fpl-icon-color': 'var(--ramp-white-1000)',
  },
  icon: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
    color: color.icon,
  },
});
